import { useContext, useState } from "react";
import { Button, Icon, Label, Select, TextField, Tooltip } from "@shopify/polaris";
import { DeleteIcon, InfoIcon, PlusIcon } from "@shopify/polaris-icons";
import { PlaceholderStepsType, StepConditionType, StepsType } from "../../apps/Bundles/Types";
import { getConditionMessage, getNumberFieldSuffix } from "../../utilities/constants";
import { BundleTypes, validateFontSize } from "../../apps/Bundles/constants";
import { PiBracketsCurlyBold } from "react-icons/pi";
import useIsMobile from "../../hooks/useIsMobile";
import { AppConfigContext } from "../../context/AppConfigContext";

const StepConditionRow = ({
    condition,
    scIdx,
    handleEditStepConditions,
    isMobile,
    money_format,
    styles,
    bundle_type,
    showPlusIcon,
}: {
    condition: StepConditionType;
    scIdx: number;
    handleEditStepConditions: (
        scIdx: number,
        key: keyof StepConditionType | "ADD" | "DELETE",
        value: string | number,
    ) => void;
    isMobile: boolean;
    money_format: string;
    styles: CSSModuleClasses;
    bundle_type: string;
    showPlusIcon: boolean;
}) => {
    const [hasInteracted, setHasInteracted] = useState(false);
    const [conditionError, setConditionError] = useState<{ limit: string }>({ limit: "" });

    return (
        <div
            style={{
                // backgroundColor: "#E8E8E8",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: showPlusIcon ? "8px" : "0",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "2px",
                }}
            >
                <Label id="review_step">
                    <span style={{ fontSize: 13 }}>{`Condition ${scIdx + 1}`}</span>
                </Label>

                {scIdx > 0 && (
                    <span
                        className={styles.stepDragIcon}
                        style={{ color: "#9b9b9b" }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditStepConditions(scIdx, "DELETE", "");
                        }}
                    >
                        <Icon source={DeleteIcon} tone={scIdx == 0 ? "inherit" : "critical"} />
                    </span>
                )}
            </div>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                    gap: "12px",
                    marginBottom: "2px",
                }}
            >
                <Select
                    label={""}
                    labelHidden
                    options={[
                        { value: "bundle_total", label: "Total Amount" },
                        { value: "items_count", label: "Total Quantity" },
                    ]}
                    onChange={(val) => {
                        handleEditStepConditions(scIdx, "basis", val);
                    }}
                    value={condition.basis || "items_count"}
                />
                <Select
                    label={""}
                    labelHidden
                    options={[
                        {
                            label: `is less than`,
                            value: "max_products",
                        },
                        {
                            label: `is equal to`,
                            value: "exact_products",
                        },
                        {
                            label: `is greater than`,
                            value: "min_products",
                        },
                    ]}
                    onChange={(val) => {
                        handleEditStepConditions(scIdx, "type", val);
                    }}
                    value={condition.type}
                />
                <TextField
                    label={""}
                    labelHidden
                    autoComplete={"off"}
                    type={"number"}
                    value={condition.limit as unknown as string}
                    suffix={condition.basis == "bundle_total" ? getNumberFieldSuffix("fixed", money_format).suffix : ""}
                    onBlur={() => {
                        if (!hasInteracted) setHasInteracted(true);
                    }}
                    onChange={(val) => {
                        const regex = /^(\d+(\.\d{0,2})?)?$/;
                        setConditionError((ps) => ({
                            ...ps,
                            limit: validateFontSize(val, condition.basis == "bundle_total" ? "Amount" : "Items count", {
                                min: 0,
                                max: 10000000,
                            }),
                        }));
                        if (!hasInteracted) setHasInteracted(true);
                        if (regex.test(val)) {
                            handleEditStepConditions(scIdx, "limit", val);
                        }
                    }}
                    error={conditionError.limit}
                />
            </div>

            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "2px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: 13,
                        }}
                    >
                        {bundle_type === BundleTypes.BB_PLACEHOLDER ? "Error Message" : "Tooltip Message"}
                        <Tooltip
                            width="wide"
                            content={
                                "This message will appear when hovering over the 'Next' button, as long as the required condition is not met."
                            }
                        >
                            <Icon source={InfoIcon} tone="subdued" />
                        </Tooltip>
                    </div>
                    <Tooltip
                        width="wide"
                        content={
                            <span
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                }}
                            >
                                <span>{`{step_label} shows the label of this step`}</span>
                                {condition.basis == "items_count" && (
                                    <span>
                                        {`{productCount} shows the number of product(s) the user is allowed to select in this step`}
                                    </span>
                                )}
                                {condition.basis == "bundle_total" && (
                                    <span>{`{amount} shows the bundle total required in the step`}</span>
                                )}
                            </span>
                        }
                    >
                        <PiBracketsCurlyBold style={{ cursor: "pointer" }} />
                    </Tooltip>
                </div>
                <TextField
                    label={""}
                    labelHidden
                    placeholder={"Enter tooltip message"}
                    autoComplete={"off"}
                    value={condition.msg}
                    onChange={(val) => {
                        handleEditStepConditions(scIdx, "msg", val);
                    }}
                    maxLength={80}
                    showCharacterCount
                />
            </div>
        </div>
    );
};

const StepConditions = ({
    step,
    handleEditStep,
    stepIdx,
    styles,
    bundle_type,
}: {
    step: StepsType | PlaceholderStepsType;
    stepIdx: number;
    handleEditStep: <K extends keyof StepsType>(idx: number, key: K | "delete", value?: any) => void;
    styles: CSSModuleClasses;
    bundle_type: string;
}) => {
    const { isMobile } = useIsMobile();
    const { money_format } = useContext(AppConfigContext);
    const isFreeGift = step?.step_content_type === "free_gift";

    const handleEditStepConditions = (
        scIdx: number,
        key: keyof StepConditionType | "ADD" | "DELETE",
        value: string | number,
    ) => {
        const updateStepConditions = [...step.step_conditions];
        if (step?.show_step_conditions === false && (step as any).substep_rules_type !== "step_level") {
            return;
        }
        if (scIdx < 0 || scIdx > updateStepConditions?.length - 1) {
            return;
        }
        switch (key) {
            case "ADD":
                if (isFreeGift) {
                    updateStepConditions.push({
                        basis: "items_count",
                        limit: 1,
                        type: "exact_products",
                        msg: getConditionMessage("items_count", "exact_products"),
                    });
                } else {
                    updateStepConditions.push({
                        basis: "items_count",
                        limit: 10,
                        type: "max_products",
                        msg: getConditionMessage("items_count", "max_products"),
                    });
                }
                break;

            case "DELETE":
                updateStepConditions.splice(scIdx, 1);
                break;
            default:
                updateStepConditions[scIdx] = { ...updateStepConditions[scIdx], [key]: value };
                if (key == "basis") {
                    // @ts-expect-error required
                    updateStepConditions[scIdx].msg = getConditionMessage(value, updateStepConditions[scIdx].type);
                }
                if (key == "type") {
                    // @ts-expect-error required
                    updateStepConditions[scIdx].msg = getConditionMessage(updateStepConditions[scIdx].basis, value);
                }
        }

        handleEditStep(stepIdx, "step_conditions", updateStepConditions);
    };
    return (
        <div
            style={{
                padding: "16px",
                backgroundColor: "#fbfafb",
                marginLeft: "25px",
                borderRadius: "8px",
                border: "1px solid #ddd",
            }}
        >
            <div
                // style={{ marginBottom: "10px" }}
                className={styles.customCardLabelWrapper}
            >
                <Label id="step_condition">
                    <span style={{ fontSize: 13 }}>
                        Add conditions on the basis of amount or product added in this step
                    </span>
                </Label>
            </div>

            <div>
                {step?.step_conditions.map((stepCondition: StepConditionType, scIdx: number) => {
                    const showPlusIcon = scIdx < step?.step_conditions?.length - 1;
                    return (
                        <div key={scIdx}>
                            <StepConditionRow
                                condition={stepCondition}
                                scIdx={scIdx}
                                money_format={money_format}
                                isMobile={isMobile}
                                handleEditStepConditions={handleEditStepConditions}
                                styles={styles}
                                bundle_type={bundle_type}
                                showPlusIcon={showPlusIcon}
                            />
                            {showPlusIcon && (
                                <span
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        // padding: "8px 0",
                                    }}
                                >
                                    <Icon source={PlusIcon} tone="primary" />
                                </span>
                            )}
                        </div>
                    );
                })}

                {step?.step_conditions?.length < 3 && (
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        style={{
                            marginTop: "12px",
                        }}
                    >
                        <Button onClick={() => handleEditStepConditions(0, "ADD", "")} fullWidth>
                            + Add Another Step Condition
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StepConditions;
